// Pronouns — subject, object (direct/indirect), reflexive, prepositional, and
// the placement rules that trip learners up. A consolidated pronoun chart sits
// at the bottom as a quick reference.
import { renderGuideSections } from "../ui/guideSections.js";

export function pronounsView(app) {
  const chart = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Pronoun Reference Chart</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Person</th>
          <th class="py-2 px-3">Subject</th>
          <th class="py-2 px-3">Direct obj.</th>
          <th class="py-2 px-3">Indirect obj.</th>
          <th class="py-2 px-3">Reflexive</th>
          <th class="py-2 px-3">After prep.</th>
        </tr></thead>
        <tbody>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">I</td><td class="py-2 px-3">eu</td><td class="py-2 px-3">me</td><td class="py-2 px-3">me</td><td class="py-2 px-3">me</td><td class="py-2 px-3">mim</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you (inf.)</td><td class="py-2 px-3">tu</td><td class="py-2 px-3">te</td><td class="py-2 px-3">te</td><td class="py-2 px-3">te</td><td class="py-2 px-3">ti</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">he / it</td><td class="py-2 px-3">ele</td><td class="py-2 px-3">o</td><td class="py-2 px-3">lhe</td><td class="py-2 px-3">se</td><td class="py-2 px-3">ele</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">she / it</td><td class="py-2 px-3">ela</td><td class="py-2 px-3">a</td><td class="py-2 px-3">lhe</td><td class="py-2 px-3">se</td><td class="py-2 px-3">ela</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you (formal)</td><td class="py-2 px-3">você</td><td class="py-2 px-3">o/a</td><td class="py-2 px-3">lhe</td><td class="py-2 px-3">se</td><td class="py-2 px-3">você</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">we</td><td class="py-2 px-3">nós</td><td class="py-2 px-3">nos</td><td class="py-2 px-3">nos</td><td class="py-2 px-3">nos</td><td class="py-2 px-3">nós</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you all (inf.)</td><td class="py-2 px-3">vós</td><td class="py-2 px-3">vos</td><td class="py-2 px-3">vos</td><td class="py-2 px-3">vos</td><td class="py-2 px-3">vós</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">they (m.)</td><td class="py-2 px-3">eles</td><td class="py-2 px-3">os</td><td class="py-2 px-3">lhes</td><td class="py-2 px-3">se</td><td class="py-2 px-3">eles</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">they (f.)</td><td class="py-2 px-3">elas</td><td class="py-2 px-3">as</td><td class="py-2 px-3">lhes</td><td class="py-2 px-3">se</td><td class="py-2 px-3">elas</td></tr>
          <tr><td class="py-2 pr-3 font-medium">you all (formal)</td><td class="py-2 px-3">vocês</td><td class="py-2 px-3">os/as</td><td class="py-2 px-3">lhes</td><td class="py-2 px-3">se</td><td class="py-2 px-3">vocês</td></tr>
        </tbody>
      </table></div>
      <p class="text-sage-500 text-xs mt-3">Special prepositional forms: <i>comigo</i> (with me), <i>contigo</i> (with you), <i>connosco</i> (with us) — never <i>com mim / com ti</i>.</p>
    </div>
  `;

  renderGuideSections(
    app,
    "Pronouns",
    "Pronouns stand in for nouns so you don't repeat them. Portuguese has a different pronoun for each grammatical role — subject, direct object, indirect object, reflexive, and the form used after a preposition.",
    [
      {
        title: "1. Subject pronouns — often dropped",
        body: `
          <p class="text-sage-700 mb-3">The verb ending already shows who the subject is, so subject pronouns are usually <b>omitted</b>: <i>Falo português</i> (not <i>Eu falo português</i>) unless you need them for emphasis or contrast.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>Emphasis:</b> <i><b>Eu</b> pago</i> — <i>I'll</i> pay (not you).</li>
            <li><b>Contrast:</b> <i>Ele trabalha mas ela estuda.</i></li>
            <li><b>tu vs você:</b> <i>tu</i> is informal and takes its own verb form (<i>tu falas</i>); <i>você</i> is more polite and takes the <i>he/she</i> verb form (<i>você fala</i>).</li>
          </ul>
        `,
      },
      {
        title: "2. Direct vs indirect object pronouns",
        body: `
          <p class="text-sage-700 mb-2">The <b>direct object</b> receives the action (<i>what?</i>); the <b>indirect object</b> is who benefits (<i>to/for whom?</i>).</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5 mb-2">
            <li><b>Direct:</b> <i>Vejo <u>o livro</u> → Vejo-o.</i> (I see it.)</li>
            <li><b>Indirect:</b> <i>Dou o livro <u>à Maria</u> → Dou-lhe o livro.</i> (I give her the book.)</li>
          </ul>
          <p class="text-sage-700 text-sm">When both appear, the indirect comes first and <b>fuses</b> with the direct (me+o→mo, te+o→to, lhe+o→lho): <i>Dou-lhe o livro → <b>Dou-lho</b>.</i></p>
        `,
      },
      {
        title: "3. Placement",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>After</b> the verb (enclisis) by default, joined with a hyphen: <i>Vejo-o. Chamo-te.</i></li>
            <li><b>Before</b> the verb (proclisis) after negation, <i>que</i>, and many adverbs: <i>Não o vejo. Que te disse? Já me viu.</i></li>
            <li><b>Inside</b> the verb (mesoclisis) in the future and conditional: <i>dar-te-ei</i> (I'll give you), <i>dir-lhe-ia</i> (I would tell him).</li>
          </ul>
        `,
      },
      {
        title: "4. Reflexive & prepositional pronouns",
        body: `
          <p class="text-sage-700 mb-2"><b>Reflexive</b> pronouns show the subject acts on itself — used with reflexive verbs: <i>levanto-me</i> (I get [myself] up), <i>chama-se</i> (he calls himself = his name is).</p>
          <p class="text-sage-700 text-sm"><b>Prepositional</b> pronouns follow words like <i>a, de, para, sem, por</i>: <i>para <b>mim</b>, sem <b>ti</b>, perto de <b>nós</b></i>. Note the special forms <i>comigo</i> and <i>contigo</i>.</p>
        `,
      },
    ],
    "",
    chart
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
